// The per-connection health pill from §s01/§s03: "Synced" (green dot),
// "Needs login" (amber dot + word), "Sync failed" or "Disconnected" (red dot
// + user-facing word), and "Manual" (neutral square). Never colour alone --
// the word always accompanies the dot, and provider error codes stay private.
import { StatusDot } from './StatusDot';
import { formatHumanDate } from '@/lib/date';

export type ConnectionStatus = 'synced' | 'needs_login' | 'error' | 'manual';

const STATUS_META: Record<ConnectionStatus, { label: string; tone: 'ok' | 'warn' | 'error' | 'neutral'; shape: 'filled' | 'square' }> = {
  synced: { label: 'Synced', tone: 'ok', shape: 'filled' },
  needs_login: { label: 'Needs login', tone: 'warn', shape: 'filled' },
  error: { label: 'Sync failed', tone: 'error', shape: 'filled' },
  manual: { label: 'Manual', tone: 'neutral', shape: 'square' },
};

export function ConnectionStatusPill({
  status,
  lastSyncedAt,
  consentExpiresAt,
  errorCode,
  className,
}: {
  status: ConnectionStatus;
  lastSyncedAt?: string | null;
  consentExpiresAt?: string | null;
  errorCode?: string | null;
  className?: string;
}) {
  const disconnected = status === 'error' && errorCode === 'connection_disconnected';
  const meta = disconnected ? { ...STATUS_META.error, label: 'Disconnected' } : STATUS_META[status];
  return (
    <span
      className={['inline-flex flex-wrap items-center gap-2 text-xs', className].filter(Boolean).join(' ')}
      data-testid="connection-status-pill"
      data-status={status}
    >
      <StatusDot tone={meta.tone} shape={meta.shape} label={meta.label} showLabel />
      {lastSyncedAt && <span className="text-muted-foreground">{status === 'synced' ? '· ' : 'Last synced '}{formatHumanDate(lastSyncedAt)}</span>}
      {consentExpiresAt && status !== 'error' && (
        <span className="text-degraded-foreground">access ends {formatHumanDate(consentExpiresAt)}</span>
      )}
    </span>
  );
}

export default ConnectionStatusPill;
